import React, { useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";
import { useClickOutside } from "@/lib/useClickOutside";

const NAV = [
  { section: "Overview", items: [{ to: "/dashboard", icon: "⊞", label: "Dashboard", testid: "nav-dashboard" }] },
  {
    section: "Proposals",
    items: [
      { to: "/new-response", icon: "➕", label: "New Response", testid: "nav-new-response" },
      { to: "/active-responses", icon: "📂", label: "Active Responses", testid: "nav-active-responses" },
      { to: "/archived-responses", icon: "🗄", label: "Archived Responses", testid: "nav-archived-responses" },
      { to: "/opportunities", icon: "🔍", label: "Opportunities", testid: "nav-opportunities" },
    ],
  },
  {
    section: "Knowledge Base",
    items: [
      { to: "/skills", icon: "🎖", label: "Skills & Certifications", testid: "nav-skills" },
      { to: "/past-performance", icon: "📋", label: "Past Performance", testid: "nav-pp" },
    ],
  },
  {
    section: "Resources",
    items: [
      { to: "/resources/learning", icon: "🎓", label: "Learning Center", testid: "nav-resources-learning" },
      { to: "/resources/community", icon: "💬", label: "Community Forum", testid: "nav-resources-community" },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        to: "/admin", icon: "🛡", testid: "nav-admin-console",
        anyPerm: ["user.manage", "kb.manage"],
        label: (hasPerm) => (hasPerm("user.manage") ? "Admin Console" : "Knowledge Base"),
      },
    ],
  },
];

function initials(name) {
  if (!name) return "U";
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function Layout() {
  const navigate = useNavigate();
  const { user, logout, hasPerm, hasAnyPerm } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);
  useClickOutside(notifRef, () => setNotifOpen(false), notifOpen);
  useClickOutside(userMenuRef, () => setUserMenuOpen(false), userMenuOpen);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-shell" data-testid="app-shell">
      <div className="topbar" data-testid="topbar">
        <div className="topbar-left">
          <button
            className="nav-toggle"
            aria-label="Toggle menu"
            data-testid="nav-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>
          <span className="logo-icon">⬡</span>
          <span className="app-name">GovCo<span>.AI</span></span>
          {user?.org_name && <span className="topbar-badge" data-testid="topbar-org">{user.org_name}</span>}
          {user?.is_superadmin && <span className="topbar-badge" style={{ background: "rgba(255,255,255,0.18)" }} title="Platform super-admin">🛡 Super-admin</span>}
        </div>
        <div className="topbar-right">
          <span style={{ fontSize: 12, opacity: 0.75 }}>{user?.role}</span>
          <div style={{ position: "relative" }} ref={notifRef}>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              data-testid="notif-btn"
              style={{ fontSize: 18, cursor: "pointer", opacity: 0.85, background: "none", border: "none", color: "inherit", padding: 0 }}
              title="Notifications"
            >
              🔔
            </button>
            {notifOpen && (
              <div
                style={{ position: "absolute", right: 0, top: 30, width: 240, background: "var(--surface, #fff)", color: "var(--text, #111)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 14, zIndex: 1000, fontSize: 13 }}
                data-testid="notif-popover"
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Notifications</div>
                <div style={{ color: "var(--text-muted)" }}>🎉 You're all caught up — no new alerts.</div>
              </div>
            )}
          </div>
          <div style={{ position: "relative" }} ref={userMenuRef}>
            <button className="avatar" title={user?.name || "Account"} onClick={() => setUserMenuOpen((v) => !v)}
              data-testid="user-menu-btn" style={{ border: "2px solid rgba(255,255,255,0.3)" }}>
              {initials(user?.name)}
            </button>
            {userMenuOpen && (
              <div
                style={{ position: "absolute", right: 0, top: 38, width: 200, background: "var(--surface, #fff)", color: "var(--text, #111)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 8, zIndex: 1000, fontSize: 13 }}
                data-testid="user-menu-popover"
              >
                <div style={{ padding: "6px 10px 10px", marginBottom: 4, borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontWeight: 700, color: "var(--text)" }}>{user?.name || "Guest"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{user?.role || ""}</div>
                </div>
                <NavLink to="/settings" onClick={() => setUserMenuOpen(false)} className="nav-item"
                  data-testid="user-menu-settings">
                  <span className="nav-icon">⚙</span> Settings
                </NavLink>
                <button onClick={doLogout} className="nav-item" data-testid="user-menu-logout"
                  style={{ color: "var(--danger, #d32f2f)" }}>
                  <span className="nav-icon">⏻</span> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="layout">
        {sidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar} data-testid="sidebar-backdrop" />}
        <nav className={`sidebar ${sidebarOpen ? "open" : ""}`} data-testid="sidebar">
          {NAV.map((sec) => {
            const items = sec.items.filter((it) => {
              if (it.anyPerm) return hasAnyPerm(it.anyPerm);
              return !it.perm || hasPerm(it.perm);
            });
            if (!items.length) return null;
            return (
              <div className="sidebar-section" key={sec.section}>
                <div className="sidebar-label">{sec.section}</div>
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/dashboard"} onClick={closeSidebar}
                    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid={it.testid}>
                    <span className="nav-icon">{it.icon}</span>
                    {typeof it.label === "function" ? it.label(hasPerm) : it.label}
                    {it.badge && <span className={`nav-badge ${it.badge.cls || ""}`}>{it.badge.text}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <main className="main" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
